/**
 * The live queue, for the desks — QA report §5.3.
 *
 * One card per clinic: who is inside now, as the largest thing on it; the
 * buttons that move the lane; who waits. Clinics reception sees every clinic
 * of the hospital, a doctor their own and a secretary their doctor's — the
 * server decides which (Backend/routes/queue.js), this only draws what came.
 *
 * Every move is pushed to every screen at once — this board, the hall TV,
 * the patient's phone — over the live socket (hooks/useLiveChannel), so the
 * QA report's "real-time when the secretary or the doctor changes the turn"
 * holds from whichever of the three screens the turn was changed.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, EmptyState, Field, Icon, Input, Modal, Select, Spinner } from '../../components/common';
import { queueApi } from '../../api/endpoints';
import { usePermissions } from '../../context/PermissionContext';
import { useNotifications } from '../../context/NotificationContext';
import { PERMISSIONS } from '../../domain/rbac';
import { useLiveChannel } from '../../hooks/useLiveChannel';
import { PatientFinder } from '../hospitalShared/PatientFinder';
import { printArea } from '../hospitalShared/labels';
import { QueueTicketSlip } from './QueueTicketSlip';

const P = PERMISSIONS;

const LIVE_LABEL = { live: 'مباشر', connecting: 'جارٍ الاتصال', fallback: 'تحديث دوري', gone: 'متوقف', offline: 'غير متصل' };

function LiveDot({ status }) {
  return (
    <span className={`qd-status qd-status--${status} text-xs text-slate-300`}>
      <i aria-hidden="true" /> {LIVE_LABEL[status] || status}
    </span>
  );
}

/** One clinic's lane. */
function LaneCard({ lane, canMove, onMove, busy, compact }) {
  const serving = lane.serving;
  const style = lane.color ? { '--clinic': lane.color } : undefined;
  const [showAll, setShowAll] = useState(false);
  const waiting = showAll ? lane.waiting : lane.waiting.slice(0, compact ? 4 : 6);

  return (
    <article className="queue-lane" style={style} data-clinic-id={lane.clinicId}>
      <header className="flex items-start gap-3">
        <span className="queue-lane__dot" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-bold text-white">{lane.name}</h3>
          {lane.doctorName && <p className="truncate text-xs text-slate-400">{lane.doctorName}</p>}
        </div>
        <div className="flex flex-wrap justify-end gap-1 text-[11px]">
          <Badge className="border-white/10 bg-white/5 text-slate-300">صدر {lane.issued}</Badge>
          <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200">انتهى {lane.done}</Badge>
        </div>
      </header>

      <div className="queue-lane__now">
        <span className="text-[11px] text-slate-400">بالداخل الآن</span>
        {serving ? (
          <>
            <span key={`${serving.id}-${serving.calledAt}`} className="queue-lane__number qd-flash" dir="ltr">
              {serving.number}
            </span>
            <span className="truncate text-sm font-bold text-white">{serving.patientName || '—'}</span>
          </>
        ) : (
          <span className="py-3 text-sm text-slate-400">لا أحد بالداخل</span>
        )}
      </div>

      {canMove && (
        <div className="flex flex-wrap gap-2">
          <Button icon="chevrons-left" loading={busy === 'next'} disabled={Boolean(busy)} onClick={() => onMove(lane, 'next')} className="queue-lane__next flex-1">
            التالي
          </Button>
          {serving && (
            <>
              <Button variant="subtle" icon="megaphone" disabled={Boolean(busy)} onClick={() => onMove(lane, 'recall', serving.id)} title="إعادة النداء على الشاشة">
                نداء
              </Button>
              <Button variant="ghost" icon="skip-forward" disabled={Boolean(busy)} onClick={() => onMove(lane, 'skip', serving.id)} title="المريض غير موجود">
                تخطي
              </Button>
              <Button variant="ghost" icon="check-circle-2" disabled={Boolean(busy)} onClick={() => onMove(lane, 'done', serving.id)} title="انتهى الكشف دون نداء التالي">
                إنهاء
              </Button>
            </>
          )}
        </div>
      )}

      <div>
        <p className="mb-1.5 text-[11px] font-bold text-slate-400">في الانتظار ({lane.waiting.length})</p>
        {lane.waiting.length === 0 ? (
          <p className="text-xs text-slate-500">لا أحد في الانتظار</p>
        ) : (
          <ul className="space-y-1">
            {waiting.map((t, i) => (
              <li key={t.id} className={`queue-row ${i === 0 ? 'queue-row--next' : ''}`}>
                <span className="queue-row__number" dir="ltr">
                  {t.number}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-slate-200">{t.patientName || '—'}</span>
                {canMove && (
                  <span className="flex gap-1">
                    <button type="button" className="queue-row__action" onClick={() => onMove(lane, 'call', t.id)} disabled={Boolean(busy)} title="استدعاء الآن" aria-label={`استدعاء ${t.number}`}>
                      <Icon name="megaphone" size={13} />
                    </button>
                    <button type="button" className="queue-row__action queue-row__action--danger" onClick={() => onMove(lane, 'cancel', t.id)} disabled={Boolean(busy)} title="إلغاء الدور" aria-label={`إلغاء ${t.number}`}>
                      <Icon name="x" size={13} />
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {lane.waiting.length > waiting.length && (
          <button type="button" className="mt-1 text-xs text-cyan-300 hover:underline" onClick={() => setShowAll(true)}>
            عرض الكل (+{lane.waiting.length - waiting.length})
          </button>
        )}
      </div>
    </article>
  );
}

/** Issue a turn by hand — a walk-in, or a booked patient who arrived. */
function IssueTicketDialog({ open, onClose, lanes }) {
  const { success, error: toastError } = useNotifications();
  const { institutionName } = usePermissions();
  const [clinicId, setClinicId] = useState('');
  const [patient, setPatient] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  const sheet = useRef(null);

  const reset = () => {
    setPatient(null);
    setName('');
    setIssued(null);
  };

  const issue = async () => {
    if (!clinicId) return toastError('اختر العيادة');
    setBusy(true);
    try {
      const ticket = await queueApi.issue({
        clinicId: Number(clinicId),
        ...(patient ? { hospitalPatientId: patient.id } : { patientName: name.trim() || null }),
      });
      setIssued(ticket);
      success(`تم إصدار الدور رقم ${ticket.number}`);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={issued ? `الدور رقم ${issued.number}` : 'إصدار دور'}
      icon="ticket"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          {issued ? (
            <>
              <Button variant="ghost" onClick={reset}>
                دور آخر
              </Button>
              <Button icon="printer" onClick={() => printArea(sheet.current)}>
                طباعة التذكرة
              </Button>
            </>
          ) : (
            <Button icon="ticket" loading={busy} onClick={issue}>
              إصدار الدور
            </Button>
          )}
        </div>
      }
    >
      {issued ? (
        <div ref={sheet} className="print-sheet receipt-sheet mx-auto max-w-md" dir="rtl">
          <QueueTicketSlip ticket={issued} hospitalName={institutionName} />
        </div>
      ) : (
        <div className="space-y-4">
          <Field label="العيادة">
            <Select value={clinicId} onChange={(e) => setClinicId(e.target.value)}>
              <option value="">اختر العيادة…</option>
              {lanes.map((l) => (
                <option key={l.clinicId} value={l.clinicId}>
                  {l.name}
                  {l.doctorName ? ` — ${l.doctorName}` : ''} ({l.waiting.length} في الانتظار)
                </option>
              ))}
            </Select>
          </Field>
          {patient ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-3 py-2">
              <span className="text-sm font-bold text-white">{patient.fullName}</span>
              <Button variant="ghost" icon="x" onClick={() => setPatient(null)} aria-label="إزالة المريض" />
            </div>
          ) : (
            <>
              <PatientFinder onSelect={setPatient} canCreate={false} />
              <Field label="أو اسم المريض فقط" hint="لمريض بلا ملف — يظهر على الشاشة مختصراً">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="الاسم" />
              </Field>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

/** The hall screens: a link per screen, which clinics it shows, and how. */
function DisplaysPanel({ lanes }) {
  const { success, error: toastError } = useNotifications();
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [qr, setQr] = useState(null);

  const load = useCallback(async () => {
    try {
      setRows(await queueApi.displays());
    } catch (err) {
      toastError(err.message);
      setRows([]);
    }
  }, [toastError]);

  useEffect(() => {
    load();
  }, [load]);

  if (rows === null) return <Spinner variant="inline" label="جارٍ تحميل الشاشات" />;

  const save = async () => {
    try {
      const created = await queueApi.createDisplay({ label: form.label, mode: form.mode, clinicIds: form.clinicIds });
      setForm(null);
      setQr(created);
      success('تم إنشاء رابط الشاشة');
      load();
    } catch (err) {
      toastError(err.message);
    }
  };

  const toggle = async (row) => {
    try {
      await queueApi.updateDisplay(row.id, { active: !row.active });
      load();
    } catch (err) {
      toastError(err.message);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      success('تم نسخ الرابط');
    } catch {
      toastError('انسخ الرابط يدوياً');
    }
  };

  const nameOf = (id) => lanes.find((l) => Number(l.clinicId) === Number(id))?.name || `#${id}`;

  return (
    <section className="glass-panel rounded-2xl border border-white/5 p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-base font-bold text-white">
          <Icon name="monitor" size={18} className="text-cyan-300" /> شاشات الانتظار
        </h3>
        <Button icon="plus" variant="subtle" onClick={() => setForm({ label: '', mode: 'number', clinicIds: [] })}>
          شاشة جديدة
        </Button>
      </header>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">لا توجد شاشات بعد. أنشئ رابطاً وافتحه على تلفزيون صالة الانتظار.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li key={row.id} className={`flex flex-wrap items-center gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3 ${row.active ? '' : 'opacity-50'}`}>
              <Icon name="tv" size={18} className="text-cyan-300" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{row.label || 'شاشة بدون اسم'}</p>
                <p className="truncate text-[11px] text-slate-400">
                  {row.clinicIds.length ? row.clinicIds.map(nameOf).join('، ') : 'كل العيادات'} · {row.mode === 'name' ? 'رقم الدور واسم المريض' : 'رقم الدور فقط'}
                </p>
              </div>
              <Button variant="ghost" icon="copy" onClick={() => copy(row.url)} aria-label="نسخ الرابط" />
              <Button variant="ghost" icon="qr-code" onClick={() => setQr(row)} aria-label="رمز الشاشة" />
              <a className="btn-ghost inline-flex items-center gap-1 text-sm" href={row.url} target="_blank" rel="noreferrer">
                <Icon name="external-link" size={14} /> فتح
              </a>
              <Button variant={row.active ? 'ghost' : 'subtle'} onClick={() => toggle(row)}>
                {row.active ? 'إيقاف' : 'تشغيل'}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={Boolean(form)}
        onClose={() => setForm(null)}
        title="شاشة انتظار جديدة"
        icon="tv"
        footer={
          <div className="flex justify-end">
            <Button icon="check" onClick={save}>
              إنشاء الرابط
            </Button>
          </div>
        }
      >
        {form && (
          <div className="space-y-4">
            <Field label="اسم الشاشة" hint="مثال: صالة الدور الأرضي">
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </Field>
            <Field label="ما يظهر مع رقم الدور">
              <Select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="number">رقم الدور فقط</option>
                <option value="name">رقم الدور والاسم الأول للمريض</option>
              </Select>
            </Field>
            <div>
              <p className="mb-1.5 text-xs font-bold text-slate-400">العيادات على هذه الشاشة (بدون اختيار = كل العيادات)</p>
              <div className="flex flex-wrap gap-1.5">
                {lanes.map((l) => {
                  const on = form.clinicIds.includes(l.clinicId);
                  return (
                    <button
                      key={l.clinicId}
                      type="button"
                      aria-pressed={on}
                      className={`chip-toggle ${on ? 'chip-toggle--on' : ''}`}
                      onClick={() =>
                        setForm({ ...form, clinicIds: on ? form.clinicIds.filter((id) => id !== l.clinicId) : [...form.clinicIds, l.clinicId] })
                      }
                    >
                      {l.name}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-slate-500">تنقسم الشاشة تلقائياً حسب عدد العيادات — 6 عيادات 3×2 و8 عيادات 4×2.</p>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(qr)} onClose={() => setQr(null)} title={qr?.label || 'رابط الشاشة'} icon="qr-code" size="sm">
        {qr && (
          <div className="space-y-3 text-center">
            {qr.qrSvg && <div className="mx-auto w-48 rounded-xl bg-white p-2" dangerouslySetInnerHTML={{ __html: qr.qrSvg }} />}
            <p className="break-all text-xs text-slate-300" dir="ltr">
              {qr.url}
            </p>
            <p className="text-xs text-slate-400">افتح الرابط على متصفح التلفزيون واضغط F11 لملء الشاشة.</p>
          </div>
        )}
      </Modal>
    </section>
  );
}

export function LiveQueueBoard({ compact = false }) {
  const { canAny } = usePermissions();
  const { error: toastError } = useNotifications();
  const [busy, setBusy] = useState(null);
  const [issuing, setIssuing] = useState(false);

  const { data, status, refresh } = useLiveChannel({ kind: 'staff', fetchSnapshot: queueApi.board, pollMs: 5000 });

  const lanes = useMemo(() => data?.clinics || [], [data]);
  const canMove = canAny(P.QUEUE_ADVANCE, P.QUEUE_OVERSEE);
  const canIssue = canAny(P.RECEIPT_ISSUE, P.APPOINTMENT_BOOK, P.QUEUE_ADVANCE, P.QUEUE_OVERSEE);
  const canScreens = canAny(P.QUEUE_OVERSEE, P.HOSPITAL_SETTINGS_MANAGE) && !compact;

  const onMove = async (lane, action, ticketId = null) => {
    setBusy(`${lane.clinicId}:${action}`);
    try {
      await queueApi.move(lane.clinicId, action, ticketId);
      // The socket brings the new board; on a fallback connection, ask now.
      if (status !== 'live') refresh();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <Spinner label="جارٍ تحميل الطابور" variant="inline" />;

  const totals = lanes.reduce(
    (acc, l) => ({ waiting: acc.waiting + l.waiting.length, serving: acc.serving + (l.serving ? 1 : 0), done: acc.done + l.done }),
    { waiting: 0, serving: 0, done: 0 },
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
          <LiveDot status={status} />
          <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-200">{totals.waiting} في الانتظار</Badge>
          <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200">{totals.serving} بالداخل</Badge>
          <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200">{totals.done} انتهى اليوم</Badge>
        </div>
        {canIssue && lanes.length > 0 && (
          <Button icon="ticket" onClick={() => setIssuing(true)}>
            إصدار دور
          </Button>
        )}
      </div>

      {lanes.length === 0 ? (
        <EmptyState icon="list-ordered" title="لا توجد عيادات" message="أضف عيادات لأطباء المنشأة لتظهر طوابيرها هنا." />
      ) : (
        <div className={`grid grid-cols-1 gap-4 ${compact ? 'lg:grid-cols-2' : 'md:grid-cols-2 2xl:grid-cols-3'}`}>
          {lanes.map((lane) => (
            <LaneCard
              key={lane.clinicId}
              lane={lane}
              compact={compact}
              canMove={canMove}
              busy={busy && busy.startsWith(`${lane.clinicId}:`) ? busy.split(':')[1] : null}
              onMove={onMove}
            />
          ))}
        </div>
      )}

      {canScreens && <DisplaysPanel lanes={lanes} />}

      <IssueTicketDialog open={issuing} onClose={() => setIssuing(false)} lanes={lanes} />
    </div>
  );
}

export default LiveQueueBoard;
